import { useCallback, useEffect, useState } from "react";
import { AutoComplete, Modal, Typography } from "antd";
import { useAuth } from "shared/contexts/AuthProvider";
import { robotsService } from "../services/robotsService";
import logger from "shared/utils/logging";

const { Text } = Typography;

/**
 * Attach an existing robot to an organization.
 *
 * Suggestions come from the robots the current user created (plus every robot
 * for superusers), but free text is still accepted: an org admin may know the
 * ID of a robot someone else registered.
 *
 * @param {Function} onOk - Called with the entered robot ID
 * @param {Array<string>} existingRobotIds - Already attached, shown as disabled
 * @param {Function} translate - Translation function (i18n key -> string)
 */
export const AttachRobotModal = ({
  visible,
  onOk,
  onCancel,
  existingRobotIds = [],
  confirmLoading = false,
  translate = (key) => key,
}) => {
  const { authFetch, isSuperUser } = useAuth();

  const [robotId, setRobotId] = useState("");
  const [candidates, setCandidates] = useState([]);

  const loadCandidates = useCallback(async () => {
    try {
      const response = isSuperUser
        ? await robotsService.getAll(authFetch, 1, 0)
        : await robotsService.getMyRobots(authFetch, 1, 0);
      setCandidates(response.data?.results ?? []);
    } catch (error) {
      // Suggestions are a convenience; typing an ID by hand still works.
      logger.warn("Failed to load robot suggestions:", error);
      setCandidates([]);
    }
  }, [authFetch, isSuperUser]);

  useEffect(() => {
    if (!visible) return;
    setRobotId("");
    loadCandidates();
  }, [visible, loadCandidates]);

  const options = candidates.map((robot) => {
    const attached = existingRobotIds.includes(robot.robot_id);
    return {
      value: robot.robot_id,
      disabled: attached,
      label: (
        <span>
          {robot.robot_id}
          {robot.name ? <Text type="secondary"> — {robot.name}</Text> : null}
          {attached ? (
            <Text type="secondary">
              {" "}
              ({translate("organizations.orgPage.alreadyAttached")})
            </Text>
          ) : null}
        </span>
      ),
    };
  });

  return (
    <Modal
      title={translate("organizations.orgPage.attachRobot")}
      open={visible}
      onOk={() => onOk(robotId.trim())}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !robotId.trim() }}
    >
      <AutoComplete
        style={{ width: "100%" }}
        value={robotId}
        options={options}
        onChange={setRobotId}
        placeholder={translate("organizations.orgPage.robotIdPlaceholder")}
        filterOption={(input, option) =>
          option.value.toLowerCase().includes(input.toLowerCase())
        }
      />
    </Modal>
  );
};
